import { api, axiosInstance } from '@/lib/api';

function unwrap<T>(body: unknown): T {
  if (body != null && typeof body === 'object' && 'data' in body && (body as { data: unknown }).data !== undefined) {
    return (body as { data: T }).data;
  }
  return body as T;
}

export type WebinarStatus = 'draft' | 'scheduled' | 'live' | 'ended';

/** Fichier partagé en direct (PDF / PowerPoint). */
export type WebinarLiveSharedDocument = {
  url: string;
  title: string;
  mimeType: string;
};

/** Champ additionnel du formulaire d’inscription publique au webinaire (e-mail + mot de passe imposés par l’app). */
export type WebinarPublicExtraField = {
  id: string;
  label: string;
  type: 'text' | 'textarea';
  required: boolean;
};

export type WebinarPublicRegistrationRow = {
  id: string;
  email: string;
  answers: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
};

export type WebinarPublicPreview = {
  title: string;
  description: string;
  scheduledStartAt: string | null;
  scheduledDurationMinutes: number | null;
  status: WebinarStatus;
  joinCode: string;
  registrationBackgroundUrl?: string | null;
};

export interface WebinarRow {
  id: string;
  title: string;
  description: string;
  joinCode: string;
  status: WebinarStatus;
  scheduledStartAt: string | null;
  /** Durée prévue en minutes (fin d’événement calendrier) ; absent / null = défaut 1 h côté calendrier. */
  scheduledDurationMinutes?: number | null;
  startedAt: string | null;
  endedAt: string | null;
  waitingRoomHtml: string | null;
  participantsCanUnmute: boolean;
  presentersCanMuteOthers: boolean;
  presenterVideoDefault: boolean;
  presenterAudioDefault: boolean;
  participantVideoDefault: boolean;
  participantAudioDefault: boolean;
  /** URL image de fond scène participant (optionnel). */
  participantStageBackgroundUrl?: string | null;
  /** URL image de fond écran d’inscription publique (optionnel). */
  registrationBackgroundUrl?: string | null;
  chatEnabled?: boolean;
  /** Accès public : inscription e-mail + mot de passe + champs optionnels (non présentateurs). */
  publicAccessEnabled?: boolean;
  publicShowOnHomepage?: boolean;
  /** @deprecated Ancien identifiant unique — ignoré par le nouveau flux. */
  publicAccessLogin?: string | null;
  publicRegistrationExtraFields?: WebinarPublicExtraField[] | null;
  createdAt?: string;
  updatedAt?: string;
  replayVideoUrl?: string | null;
  replayDurationSec?: number | null;
  replayRecordedAt?: string | null;
  createdBy?: { id: string; firstName: string; lastName: string; email: string } | null;
  presenters?: Array<{
    userId: string;
    sortOrder: number;
    user: { id: string; firstName: string; lastName: string; email: string };
  }>;
  waitingHtmlResolved?: string;
}

export interface WebinarHostGrantRow {
  id: string;
  userId: string;
  createdAt: string;
  user: { id: string; email: string; firstName: string; lastName: string; role: string | null };
}

export type LearnerWebinarSummary = {
  id: string;
  title: string;
  description: string;
  joinCode: string;
  status: WebinarStatus;
  scheduledStartAt: string | null;
  scheduledDurationMinutes?: number | null;
  startedAt: string | null;
  endedAt: string | null;
  institutionName: string;
};

export type LearnerWebinarInvitedRow = {
  invitationId: string;
  invitedAt: string;
  webinar: LearnerWebinarSummary;
};

export type LearnerWebinarPresenterRow = {
  webinar: LearnerWebinarSummary;
};

export const webinarApi = {
  eligibility: () => api.get<unknown>('/webinars/eligibility').then(unwrap<{ allowed: boolean; institutionEnabled: boolean; reason?: string }>),

  institutionSettings: () =>
    api.get<unknown>('/webinars/institution/settings').then(unwrap<{ webinarsEnabled: boolean; webinarDefaultWaitingHtml: string }>),

  patchInstitutionSettings: (body: Partial<{ webinarsEnabled: boolean; webinarDefaultWaitingHtml: string | null }>) =>
    api.patch<unknown>('/webinars/institution/settings', body).then(unwrap<{ webinarsEnabled: boolean; webinarDefaultWaitingHtml: string }>),

  listHosts: () => api.get<unknown>('/webinars/institution/hosts').then(unwrap<{ hosts: WebinarHostGrantRow[] }>),

  addHost: (userId: string) =>
    api.post<unknown>('/webinars/institution/hosts', { userId }).then(unwrap<WebinarHostGrantRow>),

  removeHost: (userId: string) => api.delete<unknown>(`/webinars/institution/hosts/${userId}`).then(() => undefined),

  institutionWebinars: () =>
    api.get<unknown>('/webinars/institution/catalog').then(unwrap<{ webinars: WebinarRow[] }>),

  mine: () => api.get<unknown>('/webinars/mine').then(unwrap<{ webinars: WebinarRow[] }>),

  invitedForMe: () =>
    api.get<unknown>('/webinars/invited-for-me').then(
      unwrap<{
        webinars: LearnerWebinarInvitedRow[];
        invited: LearnerWebinarInvitedRow[];
        presenting: LearnerWebinarPresenterRow[];
      }>,
    ),

  create: (body: Record<string, unknown>) => api.post<unknown>('/webinars', body).then(unwrap<WebinarRow>),

  update: (id: string, body: Record<string, unknown>) => api.patch<unknown>(`/webinars/${id}`, body).then(unwrap<WebinarRow>),

  remove: (id: string) => api.delete<unknown>(`/webinars/${id}`).then(() => undefined),

  detail: (id: string) => api.get<unknown>(`/webinars/${id}/detail`).then(unwrap<WebinarRow>),

  setPresenters: (id: string, userIds: string[]) =>
    api.put<unknown>(`/webinars/${id}/presenters`, { userIds }).then(unwrap<WebinarRow>),

  start: (id: string) => api.post<unknown>(`/webinars/${id}/start`, {}).then(unwrap<WebinarRow>),

  /** PDF ou PowerPoint ; le présentateur émet ensuite `webinar:document-share` après succès. */
  uploadLiveDocument: (webinarId: string, file: File) =>
    api.upload<unknown>(`/webinars/${webinarId}/live-document`, file).then(unwrap<WebinarLiveSharedDocument>),

  uploadReplay: (webinarId: string, file: File, durationSec?: number) => {
    const fd = new FormData();
    fd.append('file', file);
    if (durationSec != null && durationSec > 0) fd.append('durationSec', String(durationSec));
    return api
      .upload<unknown>(`/webinars/${webinarId}/replay`, fd)
      .then(
        unwrap<{
          replayVideoUrl: string;
          replayDurationSec: number | null;
          replayRecordedAt: string;
        }>,
      );
  },

  end: (id: string) => api.post<unknown>(`/webinars/${id}/end`, {}).then(unwrap<WebinarRow>),

  /** Traçabilité RH : CSV agrégé par participant (ou `segments` = une ligne par reconnexion). */
  downloadAttendanceExport: async (webinarId: string, options?: { segments?: boolean }) => {
    const q = options?.segments ? '?segments=1' : '';
    const res = await axiosInstance.get<Blob>(`/webinars/${webinarId}/attendance/export${q}`, {
      responseType: 'blob',
    });
    const cd = res.headers['content-disposition'];
    const raw = Array.isArray(cd) ? cd[0] : cd;
    const m = raw && /filename="([^"]+)"/.exec(raw);
    const filename = m?.[1] ?? 'webinaire-presence.csv';
    return { blob: res.data, filename };
  },

  join: (joinCode: string, options?: { gateToken?: string }) =>
    api
      .get<unknown>(
        `/webinars/join/${encodeURIComponent(joinCode)}`,
        undefined,
        options?.gateToken
          ? { headers: { 'X-Webinar-Public-Gate': options.gateToken } }
          : undefined,
      )
      .then(
        unwrap<{
          webinar: {
            id: string;
            title: string;
            description: string;
            status: WebinarStatus;
            joinCode: string;
            scheduledStartAt: string | null;
            scheduledDurationMinutes: number | null;
            startedAt: string | null;
            endedAt: string | null;
            replayVideoUrl?: string | null;
            replayDurationSec?: number | null;
            replayRecordedAt?: string | null;
            waitingHtmlResolved: string;
            participantsCanUnmute: boolean;
            presentersCanMuteOthers: boolean;
            presenterVideoDefault: boolean;
            presenterAudioDefault: boolean;
            participantVideoDefault: boolean;
            participantAudioDefault: boolean;
            participantStageBackgroundUrl: string | null;
            registrationBackgroundUrl: string | null;
            chatEnabled: boolean;
            publicAccessEnabled?: boolean;
            publicRegistrationExtraFields?: WebinarPublicExtraField[];
          };
          role: 'presenter' | 'participant';
          presenterUserIds: string[];
        }>,
      ),

  verifyPublicGate: (joinCode: string, body: { email: string; password: string; answers?: Record<string, string> }) =>
    api
      .post<unknown>(`/webinars/join/${encodeURIComponent(joinCode)}/gate`, body)
      .then(unwrap<{ token: string; isNewRegistration?: boolean }>),

  inviteCandidates: (webinarId: string, search?: string) =>
    api
      .get<unknown>(`/webinars/${webinarId}/invitations/candidates`, { search: search ?? '' })
      .then(unwrap<{ candidates: Array<{ id: string; firstName: string | null; lastName: string | null; email: string }> }>),

  listInvitations: (webinarId: string) =>
    api.get<unknown>(`/webinars/${webinarId}/invitations`).then(
      unwrap<{
        invitations: Array<{
          id: string;
          email: string;
          createdAt: string;
          invitedBy: { id: string; firstName: string | null; lastName: string | null; email: string } | null;
        }>;
      }>,
    ),

  listPublicRegistrations: (webinarId: string) =>
    api.get<unknown>(`/webinars/${webinarId}/public-registrations`).then(
      unwrap<{
        registrations: WebinarPublicRegistrationRow[];
      }>,
    ),

  inviteEmails: (webinarId: string, emails: string[]) =>
    api.post<unknown>(`/webinars/${webinarId}/invitations/email`, { emails }).then(
      unwrap<{ sent: number; duplicates: number; skipped: number; errors: string[] }>,
    ),

  inviteLearners: (webinarId: string, learnerIds: string[]) =>
    api.post<unknown>(`/webinars/${webinarId}/invitations/learners`, { learnerIds }).then(
      unwrap<{ sent: number; duplicates: number; skipped: number; errors: string[]; unknownIds: string[] }>,
    ),

  inviteGroup: (webinarId: string, groupId: string) =>
    api.post<unknown>(`/webinars/${webinarId}/invitations/group`, { groupId }).then(
      unwrap<{
        sent: number;
        duplicates: number;
        skipped: number;
        errors: string[];
        total: number;
        groupName: string;
      }>,
    ),

  homepagePublicWebinars: () =>
    api.get<unknown>('/public/webinars/homepage').then(
      unwrap<{
        webinars: Array<{
          id: string;
          title: string;
          description: string;
          joinCode: string;
          status: WebinarStatus;
          scheduledStartAt: string | null;
          scheduledDurationMinutes: number | null;
          registrationUrl: string;
        }>;
      }>,
    ),

  publicPreviewByJoinCode: (joinCode: string) =>
    api.get<unknown>(`/public/webinars/by-code/${encodeURIComponent(joinCode)}`).then(
      unwrap<{
        webinar: WebinarPublicPreview;
      }>,
    ),
};

/** postMessage iframe → parent : position du parcours (animateur webinaire). */
export const WEBINAR_PLAYER_LEADER_MSG = 'lyrra-webinar-player' as const;
/** postMessage parent → iframe : forcer le bloc courant (participants). */
export const WEBINAR_PLAYER_FOLLOW_MSG = 'lyrra-webinar-sync' as const;
